import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import { Icon, useFieldContext } from '@bridebook/aura';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover/popover';
import { usePickerSelectionFocusLock } from '../hooks/use-picker-selection-focus-lock';
import { ClearButton } from '../clear-button';
import { TimeSuggestionsPopover } from './time-suggestions-popover';
import { useSegmentedTimeInput } from './use-segmented-time-input';
import type { TimeInputProps } from './types';
import { detectBrowser24h, getNearestTimeOption, getTimeOptions } from './utils';
import { useMeasure } from '@uidotdev/usehooks';
import { cn } from '../../../lib/utils/cn';
import {
  inputBaseLayout,
  inputBaseTypography,
  inputBaseTransition,
  inputBaseRing,
  inputErrorVariant,
  inputDisabledVariant,
  inputWithLeftElementVariant,
  inputWithRightElementVariant,
} from '../input-variants';

const wrapperVariants = cva(
  cn(
    inputBaseLayout,
    'flex items-center gap-0',
    inputBaseTypography,
    inputBaseTransition,
    'data-[disabled=true]:cursor-not-allowed',
    inputBaseRing,
    'focus-within:bg-white focus-within:ring-2',
  ),
  {
    variants: {
      variant: {
        default:
          'bg-space-04 group-hover:data-[error=false]:not-focus-within:data-[disabled=false]:bg-space-08',
        white:
          'border border-space-40 bg-white focus-within:border-transparent group-hover:data-[error=false]:not-focus-within:data-[disabled=false]:bg-space-04',
      },
      error: inputErrorVariant,
      disabled: inputDisabledVariant,
      withLeftElement: inputWithLeftElementVariant,
      withRightElement: inputWithRightElementVariant,
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      disabled: false,
      withLeftElement: false,
      withRightElement: false,
    },
  },
);

const segmentClasses =
  'bg-transparent border-none outline-none p-0 text-center tabular-nums typo-base text-space placeholder:text-space-60 disabled:cursor-not-allowed';

/**
 * Desktop time input — custom segmented inputs (hour + minute) styled as one field,
 * with a popover dropdown of interval suggestions.
 */
export const TimeInputDesktop = ({
  id,
  value,
  onChange,
  onBlur,
  disabled: disabledProp,
  error: errorProp,
  step = 15,
  defaultOpenValue = '08:00',
  is24Hour: is24HourProp,
  showClearBtn = true,
  onClear,
  modal,
  ...inputProps
}: TimeInputProps) => {
  const context = useFieldContext();
  const disabled = disabledProp ?? context?.disabled ?? false;
  const error = errorProp ?? context?.error ?? false;
  const is24Hour = is24HourProp ?? detectBrowser24h();
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState<string | null>(null);
  const [disableCloseAnimationOnNextClose, setDisableCloseAnimationOnNextClose] = useState(false);
  const shouldFocusHourOnOpenRef = useRef(false);
  const skipBlurCommitRef = useRef(false);

  const { isVisuallyFocused, markSelectionClose, onTriggerFocus, onTriggerBlur } =
    usePickerSelectionFocusLock(open);

  const options = useMemo(() => getTimeOptions(step, is24Hour), [step, is24Hour]);
  const displayValue = open ? (draftValue ?? value) : value;

  const {
    hourText,
    minuteText,
    period,
    hourRef,
    minuteRef,
    periodRef,
    clear: clearSegments,
    getComposedValue,
    setFromValue,
    togglePeriod,
    hourProps,
    minuteProps,
    periodProps,
  } = useSegmentedTimeInput({
    value: displayValue,
    onChange: setDraftValue,
    is24Hour,
    step,
  });
  const parsedHourValue = parseInt(hourText, 10);
  const parsedMinuteValue = parseInt(minuteText, 10);

  const hasValue = !!value;
  const hasDisplayValue = !!displayValue;
  const showPlaceholder = !hasValue && !open;
  const shouldShowClearBtn = !!showClearBtn && hasValue;

  const getInitialDraftValue = useCallback(() => {
    if (value) return value;

    return getNearestTimeOption(defaultOpenValue, options)?.value ?? options[0]?.value ?? '';
  }, [defaultOpenValue, options, value]);

  const openPicker = useCallback(() => {
    if (disabled || open) return;

    setDisableCloseAnimationOnNextClose(false);
    const initialValue = getInitialDraftValue();
    setDraftValue(initialValue);
    setFromValue(initialValue);
    setOpen(true);
  }, [disabled, getInitialDraftValue, open, setFromValue]);

  const commitDraftValue = useCallback(
    (nextValue?: string, options?: { disableCloseAnimation?: boolean }) => {
      const composedValue = getComposedValue();
      const valueToCommit =
        nextValue ?? (composedValue !== '' ? composedValue : (draftValue ?? value));

      setDisableCloseAnimationOnNextClose(!!options?.disableCloseAnimation);

      if (valueToCommit !== value) {
        onChange(valueToCommit);
      }

      setFromValue(valueToCommit);

      setDraftValue(null);
      setOpen(false);
      shouldFocusHourOnOpenRef.current = false;
      onBlur?.();
    },
    [draftValue, getComposedValue, onBlur, onChange, setFromValue, value],
  );

  const discardDraftValue = useCallback(() => {
    setDisableCloseAnimationOnNextClose(false);
    setFromValue(value);
    setDraftValue(null);
    setOpen(false);
    shouldFocusHourOnOpenRef.current = false;
  }, [setFromValue, value]);

  const handleWrapperClick = () => {
    if (disabled) return;
    shouldFocusHourOnOpenRef.current = true;
    openPicker();

    if (open) {
      hourRef.current?.focus();
    }
  };

  const handleSelectSuggestion = (timeValue: string) => {
    markSelectionClose();
    commitDraftValue(timeValue, { disableCloseAnimation: true });
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen && disabled) return;

    if (nextOpen) {
      openPicker();
      return;
    }

    if (!open) return;

    skipBlurCommitRef.current = true;
    commitDraftValue();
  };

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    clearSegments();
    setDraftValue(null);
    setOpen(false);
    shouldFocusHourOnOpenRef.current = false;
    onChange('');
    onBlur?.();
    onClear?.(event);
  };

  // Blur handler: only fire onBlur when focus truly leaves the wrapper
  const handleBlurCapture = (e: React.FocusEvent<HTMLDivElement>) => {
    // If focus moved to another element within the wrapper, ignore
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    onTriggerBlur();

    if (skipBlurCommitRef.current) {
      skipBlurCommitRef.current = false;
      return;
    }

    if (open) {
      commitDraftValue();
    }
  };

  const handleFocusCapture = () => {
    if (disabled) return;
    onTriggerFocus();
    openPicker();
  };

  const handleKeyDownCapture = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      commitDraftValue();
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      discardDraftValue();
    }
  };

  const triggerRef = useRef<HTMLDivElement>(null);
  const [wrapperRef, { width: inputWidth }] = useMeasure();

  // Safety net: when popover closes, clear focus ring if focus is no longer inside the wrapper
  useEffect(() => {
    if (open) return;
    const id = requestAnimationFrame(() => {
      if (triggerRef.current && !triggerRef.current.contains(document.activeElement)) {
        onTriggerBlur();
      }
    });
    return () => cancelAnimationFrame(id);
  }, [open, onTriggerBlur]);

  useEffect(() => {
    if (!open || !shouldFocusHourOnOpenRef.current) return;

    const id = requestAnimationFrame(() => {
      hourRef.current?.focus();
      hourRef.current?.select();
      shouldFocusHourOnOpenRef.current = false;
    });

    return () => cancelAnimationFrame(id);
  }, [hourRef, open]);

  return (
    <Popover closeOnScroll modal={modal} open={open} onOpenChange={handleOpenChange}>
      <div className="group group/time relative" ref={wrapperRef}>
        <PopoverTrigger asChild>
          <div
            ref={triggerRef}
            role="group"
            aria-label="Time input"
            data-error={error ? 'true' : 'false'}
            data-disabled={disabled ? 'true' : 'false'}
            className={cn(
              wrapperVariants({
                error,
                disabled,
                withLeftElement: !!inputProps.icon,
                withRightElement: shouldShowClearBtn,
                variant: inputProps.variant,
              }),
              !disabled &&
                !error &&
                isVisuallyFocused &&
                'border-transparent bg-white ring-2 ring-indigo-crush',
              inputProps.className,
            )}
            onClick={handleWrapperClick}
            onKeyDownCapture={handleKeyDownCapture}
            onFocusCapture={handleFocusCapture}
            onBlurCapture={handleBlurCapture}>
            {inputProps.icon && (
              <Icon
                icon={inputProps.icon}
                className={cn(
                  'pointer-events-none absolute top-1/2 left-4 z-10 size-[16px] -translate-y-1/2 text-space',
                  inputProps.classNames?.icon,
                )}
              />
            )}

            <div className={cn('flex items-center', showPlaceholder && 'invisible')}>
              <input
                ref={hourRef}
                id={id}
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode="numeric"
                role="spinbutton"
                aria-label="Hour"
                aria-valuemin={is24Hour ? 0 : 1}
                aria-valuemax={is24Hour ? 23 : 12}
                aria-valuenow={Number.isNaN(parsedHourValue) ? undefined : parsedHourValue}
                maxLength={2}
                disabled={disabled}
                className={cn(segmentClasses, hourText.length <= 1 ? 'w-[1ch]' : 'w-[2ch]')}
                {...hourProps}
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
              />

              <span
                className={cn(
                  'typo-base text-space select-none',
                  !hasDisplayValue && 'text-space-60',
                )}
                aria-hidden>
                :
              </span>

              <input
                ref={minuteRef}
                type="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                inputMode="numeric"
                role="spinbutton"
                aria-label="Minute"
                aria-valuemin={0}
                aria-valuemax={59}
                aria-valuenow={Number.isNaN(parsedMinuteValue) ? undefined : parsedMinuteValue}
                maxLength={2}
                disabled={disabled}
                className={cn(segmentClasses, 'w-[2ch]')}
                {...minuteProps}
                onClick={(e) => {
                  e.stopPropagation();
                  openPicker();
                }}
              />

              {!is24Hour && (
                <button
                  ref={periodRef}
                  type="button"
                  aria-label="Toggle AM/PM"
                  disabled={disabled}
                  onClick={(e) => {
                    e.stopPropagation();
                    togglePeriod();
                  }}
                  className={cn(
                    'ml-1 cursor-pointer border-none bg-transparent p-0 typo-base text-space outline-none select-none focus-visible:underline disabled:cursor-not-allowed',
                    !hasDisplayValue && 'text-space-60',
                  )}
                  {...periodProps}>
                  {period.toLowerCase()}
                </button>
              )}
            </div>
          </div>
        </PopoverTrigger>

        {showPlaceholder && (
          <span
            className={cn(
              'pointer-events-none absolute top-1/2 -translate-y-1/2 typo-base text-space-60 group-focus-within/time:hidden',
              inputProps.icon ? 'left-10' : 'left-4',
            )}>
            {inputProps.placeholder}
          </span>
        )}

        {shouldShowClearBtn && <ClearButton disabled={disabled} onClick={handleClear} />}
      </div>

      <PopoverContent
        sideOffset={8}
        className="min-w-[140px] rounded-2xl bg-white py-2 shadow-bb-shadow"
        style={{ width: `${inputWidth}px` }}
        disabledCloseAnimation={disableCloseAnimationOnNextClose}
        onOpenAutoFocus={(e) => e.preventDefault()}>
        <TimeSuggestionsPopover
          activeValue={draftValue ?? value}
          onSelect={handleSelectSuggestion}
          step={step}
          defaultOpenValue={defaultOpenValue}
          is24Hour={is24Hour}
        />
      </PopoverContent>
    </Popover>
  );
};
